import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { getSmartImage } from '../../helpers/helpers';
import { searchCreators, setCreatorVip, setCreatorVipBulk } from '../../api/spotlights';

// Modal to grant Spotlight VIP: either search for a single creator (even if they have no
// spotlights / aren't in the table) or paste a comma-separated list of user IDs for bulk.
const CreatorVipModal = ({ onClose, onDone }) => {
  const [mode, setMode] = useState('search'); // 'search' | 'bulk'

  // search mode
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const debounceRef = useRef(null);

  // bulk mode
  const [bulkIds, setBulkIds] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    if (mode !== 'search') return undefined;
    if (!query.trim()) {
      setResults([]);
      return undefined;
    }
    clearTimeout(debounceRef.current);
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchCreators(query.trim())
        .then(r => setResults(r.creators || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, mode]);

  const makeOneVip = async creator => {
    if (creator.isSpotlightVip) return;
    setSavingId(creator.User_id);
    try {
      await setCreatorVip(creator.User_id, true);
      cogoToast.success(`@${creator.username || creator.User_id} is now a Spotlight VIP`);
      setResults(results.map(r => (r.User_id === creator.User_id ? { ...r, isSpotlightVip: true } : r)));
      onDone && onDone();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to set VIP.');
    } finally {
      setSavingId(null);
    }
  };

  const submitBulk = async () => {
    const ids = bulkIds
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(Boolean);
    if (!ids.length) return cogoToast.error('Enter at least one User ID.');
    setBulkSaving(true);
    try {
      const r = await setCreatorVipBulk(ids, true);
      const skipped = (r.results || []).filter(x => x.status === 'no_settings').map(x => x.userId);
      cogoToast.success(`Made ${r.updated} creator(s) VIP${skipped.length ? ` · ${skipped.length} skipped (no settings: ${skipped.join(', ')})` : ''}`);
      onDone && onDone();
      if (!skipped.length) onClose();
      else setBulkIds(skipped.join(', '));
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to set VIP.');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className='sl-modal-overlay' onMouseDown={onClose}>
      <div className='sl-modal' onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>Make Spotlight VIP</div>
          <button className='sl-modal-close' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body'>
          <div className='sl-segmented' style={{ marginBottom: 18 }}>
            <div className={cn('sl-segment', { active: mode === 'search' })} onClick={() => setMode('search')}>
              Search a creator
            </div>
            <div className={cn('sl-segment', { active: mode === 'bulk' })} onClick={() => setMode('bulk')}>
              Bulk by IDs
            </div>
          </div>

          {mode === 'search' ? (
            <>
              <input
                className='sl-search'
                style={{ width: '100%' }}
                type='text'
                autoFocus
                placeholder='Search by username, name, or ID…'
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              <div className='sl-vip-results'>
                {searching && <div className='sl-empty' style={{ padding: 20 }}>Searching…</div>}
                {!searching &&
                  results.map(c => (
                    <div className='sl-vip-result' key={c.User_id}>
                      <div className='sl-vip-result-id'>
                        {getSmartImage(c.image) ? <img src={getSmartImage(c.image)} alt='' /> : <div className='sl-vip-avatar-placeholder' />}
                        <div>
                          <div className='name'>{c.username ? `@${c.username}` : c.name || c.User_id}</div>
                          <div className='sub'>{c.name && c.username ? c.name : `User ${c.User_id}`}</div>
                        </div>
                      </div>
                      {c.isSpotlightVip ? (
                        <span className='sl-pill amber'>★ VIP</span>
                      ) : (
                        <button className='sl-btn small' disabled={savingId === c.User_id} onClick={() => makeOneVip(c)}>
                          Make VIP
                        </button>
                      )}
                    </div>
                  ))}
                {!searching && query.trim() && !results.length && <div className='sl-empty' style={{ padding: 20 }}>No matches.</div>}
              </div>
            </>
          ) : (
            <>
              <div className='sl-card-label' style={{ marginBottom: 8 }}>User IDs (comma-separated)</div>
              <textarea
                className='sl-vip-textarea'
                autoFocus
                placeholder='e.g. 157843, 26134, 171887'
                value={bulkIds}
                onChange={e => setBulkIds(e.target.value)}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button className='sl-btn' disabled={bulkSaving || !bulkIds.trim()} onClick={submitBulk}>
                  {bulkSaving ? 'Working…' : 'Make VIP'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorVipModal;
